import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileCheck, AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number): string {
  if (v >= 0) return `$${v.toLocaleString()}`;
  return `-$${Math.abs(v).toLocaleString()}`;
}

const ISSUE_ICONS: Record<string, any> = { error: XCircle, warning: AlertTriangle, info: Info };
const ISSUE_COLORS: Record<string, string> = {
  error: 'border-l-red-500 bg-red-500/5 text-red-600 dark:text-red-400',
  warning: 'border-l-yellow-500 bg-yellow-500/5 text-yellow-600 dark:text-yellow-400',
  info: 'border-l-blue-500 bg-blue-500/5 text-blue-600 dark:text-blue-400',
};

export default function BASValidatorPage() {
  const [selectedClientId, setSelectedClientId] = useState('');

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-bas'],
    queryFn: async () => { const r = await apiClient.get('/clients?limit=500'); return r.data.data; },
  });
  const clients = (clientsData?.clients || clientsData || []).filter((c: any) => c.xeroOrgId || c.xero_org_id);

  const { data, isLoading } = useQuery({
    queryKey: ['bas-validate', selectedClientId],
    queryFn: async () => { const r = await apiClient.get(`/bas/validate/${selectedClientId}`); return r.data.data; },
    enabled: !!selectedClientId,
  });

  const gst = data?.gst;
  const bas = data?.bas;
  const issues = data?.issues || [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileCheck}
        title="BAS Validator"
        description="Validate GST calculations against Xero data before lodgement"
        iconColor="bg-blue-500/10 text-blue-500"
      />


      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium whitespace-nowrap">Select Client:</label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="w-[350px]">
                <SelectValue placeholder="Choose a Xero-connected client..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {clients.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>{c.displayName || c.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <span className="ml-3 text-sm text-muted-foreground">Fetching GST data from Xero...</span>
        </div>
      )}

      {data?.status === 'no_xero' && (
        <Card className="border-yellow-500/30"><CardContent className="pt-4 pb-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          <p className="text-sm">{data.message}</p>
        </CardContent></Card>
      )}

      {gst && (
        <>
          {/* Period Info */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Badge variant="outline">{data.previousQuarter}</Badge>
            <span>Period: {data.period?.from} to {data.period?.to}</span>
          </div>

          {/* GST Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Total Sales</p>
                <p className="text-lg font-bold">{fmtCurrency(gst.totalSales)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">GST Collected</p>
                <p className="text-lg font-bold text-red-500">{fmtCurrency(gst.gstCollected)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Total Purchases</p>
                <p className="text-lg font-bold">{fmtCurrency(gst.totalPurchases)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">GST Paid (Credits)</p>
                <p className="text-lg font-bold text-green-500">{fmtCurrency(gst.gstPaid)}</p>
              </CardContent>
            </Card>
            <Card className="border-primary/30">
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-primary uppercase">Net GST Payable</p>
                <p className="text-lg font-bold text-primary">{fmtCurrency(gst.netGST)}</p>
              </CardContent>
            </Card>
          </div>

          {/* BAS Labels */}
          {bas && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Estimated BAS Labels</CardTitle>
                <CardDescription className="text-xs">These are estimates based on Xero data. Verify before lodging.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-4 py-2 text-left font-medium">Label</th>
                      <th className="px-4 py-2 text-left font-medium">Description</th>
                      <th className="px-4 py-2 text-right font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b"><td className="px-4 py-2 font-medium">1A</td><td className="px-4 py-2 text-muted-foreground">Total sales (including GST)</td><td className="px-4 py-2 text-right">{fmtCurrency(bas.label1a)}</td></tr>
                    <tr className="border-b"><td className="px-4 py-2 font-medium">1B</td><td className="px-4 py-2 text-muted-foreground">GST on sales</td><td className="px-4 py-2 text-right">{fmtCurrency(bas.label1b)}</td></tr>
                    <tr className="border-b"><td className="px-4 py-2 font-medium">11</td><td className="px-4 py-2 text-muted-foreground">Total purchases (including GST)</td><td className="px-4 py-2 text-right">{fmtCurrency(bas.label11)}</td></tr>
                    <tr className="border-b"><td className="px-4 py-2 font-medium">11B</td><td className="px-4 py-2 text-muted-foreground">GST on purchases (input tax credits)</td><td className="px-4 py-2 text-right">{fmtCurrency(bas.label11b)}</td></tr>
                    <tr className="bg-primary/5"><td className="px-4 py-2 font-bold">9</td><td className="px-4 py-2 font-medium">Net GST payable / (refundable)</td><td className="px-4 py-2 text-right font-bold text-primary">{fmtCurrency(bas.label9)}</td></tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Issues */}
          {issues.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Validation Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {issues.map((issue: any, i: number) => {
                  const Icon = ISSUE_ICONS[issue.type] || Info;
                  return (
                    <div key={i} className={cn('rounded-lg p-3 border-l-4', ISSUE_COLORS[issue.type])}>
                      <p className="text-xs font-semibold flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5" /> {issue.title}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{issue.detail}</p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
